import { Capacitor } from "@capacitor/core";
import { Typography, Box, makeStyles } from "@material-ui/core";
import * as React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useRecoilState, useSetRecoilState } from "recoil";

import { RegistrationMethod, UserRegistrations } from "@/api/generated";
import AppleLoginButton from "@/components/AppleLoginButton";
import { CustomButton } from "@/components/Button";
import { CustomForm } from "@/components/Form";
import GoogleLoginButton from "@/components/GoogleLoginButton";
import { RegistrationPhases } from "@/components/registration/RegistrationPhases";
import { TextLink } from "@/components/TextLink";
import { Toast } from "@/components/Toast";
import { RegistrationFlowLayout } from "@/layouts/RegistrationFlowLayout";
import theme from "@/theme";
import { RegisterFullFormData } from "@/validations/register";
import {
  RegistrationMethodState,
  UserRegistrationsAppleFormState,
  UserRegistrationsFormState,
  UserRegistrationsGoogleFormState,
} from "@/view_models/atoms";

const useStyles = makeStyles(() => ({
  content: { display: "flex", flexDirection: "column", gap: 24 },
  social: { display: "flex", flexDirection: "column", gap: 12 },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    fontSize: 12,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    "&::before, &::after": {
      content: "\"\"",
      flex: 1,
      height: 1,
      backgroundColor: theme.palette.semantic["border/default"],
    },
    [theme.breakpoints.up("md")]: { fontSize: 14 },
  },
  fields: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    "& .MuiFormControl-root": { margin: 0 },
    [theme.breakpoints.up("md")]: {
      "&& .MuiOutlinedInput-input": { fontSize: 16 },
      "&& .MuiTypography-h5": { fontSize: 14 },
    },
  },
  secondaryText: {
    fontSize: 12,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    textAlign: "center",
    [theme.breakpoints.up("md")]: { fontSize: 14 },
  },
  terms: { "&& a": { fontSize: "inherit", fontWeight: 400 } },
  bottom: { display: "flex", flexDirection: "column", gap: 12, marginTop: 32 },
  action: { width: "100%", margin: "0 auto", [theme.breakpoints.up("md")]: { maxWidth: 360 } },
  login: { display: "flex", justifyContent: "center", alignItems: "center", flexWrap: "wrap", gap: 8 },
  loginLink: { display: "inline-flex", alignItems: "center", gap: 8 },
}));

interface RegisterBasicPageProps {
  onNext: () => void;
  validate: () => Promise<boolean>;
}

const RegisterBasicPage: React.FC<RegisterBasicPageProps> = ({ onNext, validate }) => {
  const classes = useStyles();
  const {
    control,
    formState: { errors },
    getValues,
  } = useFormContext<RegisterFullFormData>();
  const [formData, setFormData] = useRecoilState(UserRegistrationsFormState);
  const setGoogleFormData = useSetRecoilState(UserRegistrationsGoogleFormState);
  const setAppleFormData = useSetRecoilState(UserRegistrationsAppleFormState);
  const setRegistrationMethod = useSetRecoilState(RegistrationMethodState);
  const [toastError, setToastError] = React.useState<string>("");

  const handleNext = React.useCallback(async () => {
    const data = getValues();
    const isValid = await validate();
    if (isValid) {
      setFormData({ ...data, password_confirmation: data.password } as UserRegistrations);
      setRegistrationMethod(RegistrationMethod.Email);
      onNext();
    }
  }, [getValues, validate, onNext]);

  // Googleログイン処理
  const handleSuccessGoogle = (socialLoginResponse) => {
    setGoogleFormData((prev) => ({
      ...prev,
      social_login_response: socialLoginResponse,
    }));
    setRegistrationMethod(RegistrationMethod.Google);
    onNext();
  };

  // Appleログイン処理
  const handleSuccessApple = (socialLoginResponse) => {
    setAppleFormData((prev) => ({
      ...prev,
      social_login_response: socialLoginResponse,
    }));
    setRegistrationMethod(RegistrationMethod.Apple);
    onNext();
  };

  const handleError = () => {
    setToastError("外部サービスでのログインに失敗しました");
  };

  // トーストのクローズ
  const handleClose = () => {
    setToastError("");
  };

  return (
    <RegistrationFlowLayout
      phaseTags={<RegistrationPhases completed />}
      title="診断結果を保存しましょう"
      description={"無料でアカウントを作成すると、診断結果の保存と\n物件・プランナーの閲覧ができます"}
      descriptionSize="small"
    >
      <Box className={classes.content}>
        <Box className={classes.social}>
          <GoogleLoginButton v2 onSuccess={handleSuccessGoogle} onError={handleError} />
          {Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios" && (
            // AppleログインはiOSアプリのみ表示
            <AppleLoginButton v2 onSuccess={handleSuccessApple} onError={handleError} />
          )}
        </Box>
        <Box className={classes.divider}>または</Box>
        <Box className={classes.fields}>
          <Controller
            name="email"
            control={control}
            defaultValue={formData.email || ""}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="メールアドレス"
                type="text"
                autoComplete="email"
                error={!!errors.email}
                errorText={Boolean(errors.email) && errors.email.message}
                placeHolder="propally@example.com"
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            defaultValue={formData.password || ""}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="パスワード"
                type="password"
                autoComplete="new-password"
                error={!!errors.password}
                helperText="半角の英字と数字を含む8〜72文字"
                helperTextPlacement="below"
                errorText={
                  Boolean(errors.password) && <span dangerouslySetInnerHTML={{ __html: errors.password.message }} />
                }
                placeHolder="半角英数8文字以上"
              />
            )}
          />
        </Box>
        <Typography className={`${classes.secondaryText} ${classes.terms}`}>
          <TextLink arrow={false} href="https://lp.propally.jp/termsofuse.html" targetBlank>
            利用規約
          </TextLink>
          と
          <TextLink arrow={false} href="https://propally.co.jp/privacypolicy.html" targetBlank>
            プライバシーポリシー
          </TextLink>
          に同意の上、お進みください
        </Typography>
      </Box>
      <Box className={classes.bottom}>
        <Box className={classes.action}>
          <CustomButton fullWidth buttonSize="L" type="button" onClick={handleNext}>
            同意して次へ
          </CustomButton>
        </Box>
        <Box className={`${classes.secondaryText} ${classes.login}`}>
          <span>アカウントをお持ちの方は</span>
          <TextLink arrow={false} href="/login">
            <span className={classes.loginLink}>
              ログイン
              <img src="/icons/ds-v2-link-chevron.svg" alt="" />
            </span>
          </TextLink>
        </Box>
      </Box>
      {/* エラーメッセージ表示用 */}
      <Toast open={Boolean(toastError)} variant="error" message={toastError} onClose={handleClose} />
    </RegistrationFlowLayout>
  );
};

export default RegisterBasicPage;
